import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
    Box,
    Typography,
    Paper,
    Stack,
    TextField,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Chip,
    Skeleton,
    Avatar,
    IconButton,
    InputAdornment,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Grid,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Checkbox,
    FormGroup,
    FormControlLabel,
    CircularProgress,
    Alert
} from '@mui/material';
import { Search, Add, Edit, Delete, Person, ContentCopy as CopyIcon } from '@mui/icons-material';
import { useVendorStaff, useCreateStaff, useUpdateStaff, useDeleteStaff } from 'hooks/useStaff';
import PermissionGuard from 'ui-component/PermissionGuard';

const PERMISSIONS = [
    { value: 'manage_inventory', label: 'Manage Inventory' },
    { value: 'manage_orders', label: 'Manage Orders' },
    { value: 'manage_staff', label: 'Manage Staff' },
    { value: 'manage_finance', label: 'Manage Finance' },
    { value: 'view_analytics', label: 'View Analytics' }
];

export default function VendorStaffList() {
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [search, setSearch] = useState('');
    const [modalOpen, setModalOpen] = useState(false);
    const [editingStaff, setEditingStaff] = useState(null);
    const [successData, setSuccessData] = useState(null);

    const {
        data: staffData,
        isLoading
    } = useVendorStaff({
        page: page + 1,
        limit: rowsPerPage,
        search: search || undefined
    });

    const createMutation = useCreateStaff();
    const updateMutation = useUpdateStaff();
    const deleteMutation = useDeleteStaff();

    const staffs = staffData?.data || [];
    const totalStaffs = staffData?.total || 0;

    const handleOpenModal = (staff = null) => {
        setEditingStaff(staff);
        setModalOpen(true);
    };

    const handleCloseModal = () => {
        setEditingStaff(null);
        setModalOpen(false);
    };

    const handleDelete = async (id) => {
        if (window.confirm('Are you sure you want to remove this staff member?')) {
            await deleteMutation.mutateAsync(id);
        }
    };

    return (
        <Box p={3}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={4}>
                <Typography variant="h4" fontWeight={700}>
                    Staff Management
                </Typography>
                <PermissionGuard permission="manage_staff" action="create">
                    <Button variant="contained" startIcon={<Add />} onClick={() => handleOpenModal()}>
                        Add New Staff
                    </Button>
                </PermissionGuard>
            </Stack>

            {successData && (
                <Alert
                    severity="success"
                    sx={{ mb: 3, borderRadius: 2 }}
                    onClose={() => setSuccessData(null)}
                    action={
                        <Button
                            color="inherit"
                            size="small"
                            onClick={() => {
                                navigator.clipboard.writeText(successData.tempPassword);
                            }}
                            startIcon={<CopyIcon />}
                        >
                            Copy Password
                        </Button>
                    }
                >
                    Staff member <strong>{successData.name}</strong> has been added successfully!
                    <br />
                    Temporary Password: <strong>{successData.tempPassword}</strong>
                </Alert>
            )}

            <Paper sx={{ width: '100%', borderRadius: 3, p: 2 }}>
                <TextField
                    placeholder="Search staff by name or email..."
                    size="small"
                    sx={{ width: 350, mb: 3 }}
                    onChange={(e) => setSearch(e.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <Search fontSize="small" />
                            </InputAdornment>
                        )
                    }}
                />

                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Staff Member</TableCell>
                                <TableCell>Role</TableCell>
                                <TableCell>Permissions</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell align="right">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {isLoading
                                ? [...Array(5)].map((_, i) => (
                                      <TableRow key={i}>
                                          <TableCell colSpan={5}>
                                              <Skeleton height={50} />
                                          </TableCell>
                                      </TableRow>
                                  ))
                                : staffs.map((staff) => (
                                      <TableRow key={staff.id} hover>
                                          <TableCell>
                                              <Stack direction="row" spacing={2} alignItems="center">
                                                  <Avatar sx={{ bgcolor: 'primary.light' }}>
                                                      <Person fontSize="small" />
                                                  </Avatar>
                                                  <Box>
                                                      <Typography variant="subtitle2" fontWeight={600}>
                                                          {staff.user?.firstName} {staff.user?.lastName}
                                                      </Typography>
                                                      <Typography variant="caption" color="text.secondary">
                                                          {staff.user?.email}
                                                      </Typography>
                                                  </Box>
                                              </Stack>
                                          </TableCell>
                                          <TableCell>
                                              <Chip label={staff.role?.toUpperCase()} size="small" variant="outlined" />
                                          </TableCell>
                                          <TableCell>
                                              <Stack direction="row" spacing={0.5} flexWrap="wrap">
                                                  {staff.permissions?.map((p) => (
                                                      <Chip
                                                          key={p}
                                                          label={p.replace('manage_', '').replace('_', ' ')}
                                                          size="small"
                                                          sx={{ fontSize: '0.65rem' }}
                                                      />
                                                  ))}
                                              </Stack>
                                          </TableCell>
                                          <TableCell>
                                              <Chip
                                                  label={staff.status}
                                                  size="small"
                                                  color={staff.status === 'active' ? 'success' : 'error'}
                                              />
                                          </TableCell>
                                          <TableCell align="right">
                                              <IconButton onClick={() => handleOpenModal(staff)} disabled={updateMutation.isPending}>
                                                  <Edit fontSize="small" />
                                              </IconButton>
                                              <IconButton
                                                  color="error"
                                                  onClick={() => handleDelete(staff.id)}
                                                  disabled={deleteMutation.isPending}
                                              >
                                                  {deleteMutation.isPending && deleteMutation.variables === staff.id ? (
                                                      <CircularProgress size={20} />
                                                  ) : (
                                                      <Delete fontSize="small" />
                                                  )}
                                              </IconButton>
                                          </TableCell>
                                      </TableRow>
                                  ))}
                        </TableBody>
                    </Table>
                </TableContainer>

                <TablePagination
                    component="div"
                    count={totalStaffs}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={(_, p) => setPage(p)}
                    onRowsPerPageChange={(e) => setRowsPerPage(parseInt(e.target.value, 10))}
                />
            </Paper>

            <StaffFormModal
                open={modalOpen}
                onClose={handleCloseModal}
                staff={editingStaff}
                createMutation={createMutation}
                updateMutation={updateMutation}
                onSuccess={(data) => setSuccessData(data)}
            />
        </Box>
    );
}

function StaffFormModal({ open, onClose, staff, createMutation, updateMutation, onSuccess }) {
    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        phoneNumber: '',
        role: 'staff',
        permissions: []
    });
    const [localError, setLocalError] = useState('');

    useEffect(() => {
        setLocalError('');
        if (staff) {
            setFormData({
                firstName: staff.user?.firstName || '',
                lastName: staff.user?.lastName || '',
                email: staff.user?.email || '',
                phoneNumber: staff.user?.phoneNumber || '',
                role: staff.role || 'staff',
                permissions: staff.permissions || []
            });
        } else {
            setFormData({
                firstName: '',
                lastName: '',
                email: '',
                phoneNumber: '',
                role: 'staff',
                permissions: []
            });
        }
    }, [staff, open]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
        setLocalError('');
    };

    const handlePermissionChange = (perm) => {
        setFormData((prev) => {
            const has = prev.permissions.includes(perm);
            return {
                ...prev,
                permissions: has ? prev.permissions.filter((p) => p !== perm) : [...prev.permissions, perm]
            };
        });
    };

    const handleSubmit = async () => {
        setLocalError('');
        try {
            if (staff) {
                await updateMutation.mutateAsync({ id: staff.id, payload: formData });
            } else {
                const res = await createMutation.mutateAsync(formData);
                if (res.tempPassword) {
                    onSuccess({
                        tempPassword: res.tempPassword,
                        name: `${formData.firstName} ${formData.lastName}`
                    });
                }
            }
            onClose();
        } catch (err) {
            setLocalError(err.response?.data?.message || 'Failed to process request');
            console.error(err);
        }
    };

    const isPending = createMutation.isPending || updateMutation.isPending;

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>{staff ? 'Edit Staff Member' : 'Add New Staff Member'}</DialogTitle>
            <DialogContent dividers>
                {localError && (
                    <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
                        {localError}
                    </Alert>
                )}
                <Grid container spacing={2} pt={1}>
                    {!staff && (
                        <>
                            <Grid item xs={6}>
                                <TextField
                                    fullWidth
                                    label="First Name"
                                    name="firstName"
                                    value={formData.firstName}
                                    onChange={handleChange}
                                    disabled={isPending}
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <TextField
                                    fullWidth
                                    label="Last Name"
                                    name="lastName"
                                    value={formData.lastName}
                                    onChange={handleChange}
                                    disabled={isPending}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Email"
                                    name="email"
                                    value={formData.email}
                                    onChange={handleChange}
                                    disabled={isPending}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Phone Number"
                                    name="phoneNumber"
                                    value={formData.phoneNumber}
                                    onChange={handleChange}
                                    disabled={isPending}
                                />
                            </Grid>
                        </>
                    )}
                    <Grid item xs={12}>
                        <FormControl fullWidth>
                            <InputLabel>Role</InputLabel>
                            <Select name="role" value={formData.role} label="Role" onChange={handleChange} disabled={isPending}>
                                <MenuItem value="staff">Staff</MenuItem>
                                <MenuItem value="manager">Manager</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                        <Typography variant="subtitle2" mb={1}>
                            Permissions
                        </Typography>
                        <FormGroup>
                            <Grid container>
                                {PERMISSIONS.map((p) => (
                                    <Grid item xs={6} key={p.value}>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={formData.permissions.includes(p.value)}
                                                    onChange={() => handlePermissionChange(p.value)}
                                                    disabled={isPending}
                                                />
                                            }
                                            label={p.label}
                                        />
                                    </Grid>
                                ))}
                            </Grid>
                        </FormGroup>
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={isPending}>
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={isPending}
                    startIcon={isPending && <CircularProgress size={20} color="inherit" />}
                >
                    {isPending ? 'Processing...' : staff ? 'Update Staff' : 'Create Staff'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

StaffFormModal.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onSuccess: PropTypes.func.isRequired,
    staff: PropTypes.shape({
        id: PropTypes.string,
        role: PropTypes.string,
        permissions: PropTypes.arrayOf(PropTypes.string),
        user: PropTypes.shape({
            firstName: PropTypes.string,
            lastName: PropTypes.string,
            email: PropTypes.string,
            phoneNumber: PropTypes.string
        })
    }),
    createMutation: PropTypes.shape({
        mutateAsync: PropTypes.func.isRequired,
        isPending: PropTypes.bool
    }).isRequired,
    updateMutation: PropTypes.shape({
        mutateAsync: PropTypes.func.isRequired,
        isPending: PropTypes.bool
    }).isRequired
};
